import { debounce, get } from 'lodash'
import PropTypes from 'prop-types'
import { Component } from 'react'

import defaultProps from '@/components/Form/defaultProps'
import propTypes, { formik } from '@/components/Form/propTypes'

import './index.scss'

// @ts-expect-error
import { Editor } from 'slate-react'

import { Icon, Toolbar } from './Components'
import HoverMenu from './HoverMenu'
import { deserialize, serialize } from './slate-to-markdown-serializer'
import ToolbarButton from './ToolbarButton'
import { getLinks, renderMark, renderNode } from './utils'

class Slate extends Component<any, any> {
  static displayName = 'Slate'

  static propTypes = {
    ...propTypes,
    formik: formik.isRequired,
    fontTypeToolbar: PropTypes.bool,
    linkToolbar: PropTypes.bool,
    bulletedListToolbar: PropTypes.bool,
    showUrlNote: PropTypes.bool,
    numberedListToolbar: PropTypes.bool
  }

  static defaultProps = {
    ...defaultProps,
    fontTypeToolbar: true,
    linkToolbar: true,
    bulletedListToolbar: true,
    showUrlNote: false,
    numberedListToolbar: false
  }

  editor: any
  menu: any

  constructor(props: any) {
    super(props)

    //the serializer adds some zero width whitespace to make the card match the editor
    //we need to remove it so the parser can do its job
    const markdown = this.removeMarkdownWhitespacing(get(props.formik.values, props.name, ''))

    this.state = {
      value: deserialize(markdown),
      link: ''
    }
  }

  componentDidMount = () => {
    this.updateMenu()
  }

  componentDidUpdate = (prevProps: any) => {
    this.updateMenu()
    if (this.props.name !== prevProps.name) {
      const markdown = this.removeMarkdownWhitespacing(get(this.props.formik.values, this.props.name, ''))

      this.setState({
        value: deserialize(markdown)
      })
    }
  }

  removeMarkdownWhitespacing = (markdown: any) => {
    if (!markdown) return markdown

    // API gets sent x2 newlines, so need to replace with a single one
    return markdown.replaceAll('\n\n', '\n').replaceAll('​', '')
  }

  ref = (editor: any) => {
    this.editor = editor

    //need to force an update for the toolbar buttons
    this.forceUpdate()
  }

  getToolbarButtons = (value: any) => {
    const { fontTypeToolbar, linkToolbar, bulletedListToolbar, numberedListToolbar } = this.props
    const toolbarButtons = []

    if (fontTypeToolbar)
      toolbarButtons.push(
        <span key="font-type" className="button-group">
          <ToolbarButton value={value} editor={this.editor} type="bold">
            <Icon className="format_bold" />
          </ToolbarButton>
          <ToolbarButton value={value} editor={this.editor} type="italic">
            <Icon className="format_italic" />
          </ToolbarButton>
        </span>
      )

    if (linkToolbar)
      toolbarButtons.push(
        <span key="links" className="button-group">
          <ToolbarButton value={value} editor={this.editor} type="link">
            <Icon className="format_link" />
          </ToolbarButton>
        </span>
      )

    if (bulletedListToolbar)
      if (numberedListToolbar) {
        toolbarButtons.push(
          <span key="list" className="button-group">
            <ToolbarButton value={value} editor={this.editor} type="bulleted-list" iconClass="bulleted-list">
              <Icon className="format_bulleted-list" />
            </ToolbarButton>
            <ToolbarButton value={value} editor={this.editor} type="numbered-list" iconClass="numbered-list">
              <Icon className="format_numbered-list" />
            </ToolbarButton>
          </span>
        )
      } else {
        toolbarButtons.push(
          <span key="list" className="button-group">
            <ToolbarButton value={value} editor={this.editor} type="bulleted-list" iconClass="bulleted-list">
              <Icon className="format_bulleted-list" />
            </ToolbarButton>
          </span>
        )
      }

    return toolbarButtons
  }

  hoverMenuInnerReference = (menu: any) => {
    this.menu = menu
  }

  shouldComponentUpdate(nextProps: any, nextState: any) {
    return (
      this.props.name !== nextProps.name ||
      get(this.props.formik.values, this.props.name) !== get(nextProps.formik.values, nextProps.name) ||
      this.state.value !== nextState.value
    )
  }

  onBlur = () => {
    if (!get(this.props.formik.touched, this.props.name)) {
      this.props.formik.setFieldTouched(this.props.name, true)
    }
    this.props.onBlur && this.props.onBlur(get(this.props.formik.values, this.props.name))
  }

  updateLink = (val: any) => {
    const { value } = this.state
    const editor = this.editor
    const link = value.inlines.find((inline: any) => inline.type === 'link')

    if (link) {
      editor.setNodeByKey(link.key, { data: { href: val } })
      editor.moveToRangeOfNode(link)
      editor.moveToEnd()
    }
  }

  setMarkdown = debounce(value => {
    const markdown = serialize(value)
    this.props.formik.setFieldValue(this.props.name, markdown)
  }, 75)

  onChange = ({ value }: any) => {
    if (value.document !== this.state.value.document) {
      this.setMarkdown(value)
    }

    const links = getLinks(value)
    this.setState({ value, link: links.length === 1 ? links[0].href : '' })
  }

  updateMenu = () => {
    if (!this.menu || !this.menu.getInnerRef()) {
      return
    }
    const menu = this.menu.getInnerRef()

    const { value } = this.state

    const native = window.getSelection()
    const links = getLinks(value)

    if (!native || links.length !== 1) {
      menu.removeAttribute('style')
      return
    }

    const range = native.getRangeAt(0)
    const rect = range.getBoundingClientRect()

    if (rect.x === 0 && rect.y === 0 && rect.width === 0 && rect.height === 0) {
      menu.removeAttribute('style')
      return
    }

    menu.style.opacity = 1
    menu.style.top = `${rect.top + window.pageYOffset + menu.offsetHeight / 2 + 4}px`
    menu.style.left = `${rect.left + window.pageXOffset - menu.offsetWidth / 2 + rect.width / 2}px`

    if (links[0].href === '') {
      this.menu.focus()
    }
  }

  renderEditor = (props: any, editor: any, next: any) => {
    const children = next()
    return (
      <>
        {children}
        <HoverMenu
          className="slate-rich-text-editor"
          placeholder="http://www.example.com"
          ref={this.hoverMenuInnerReference}
          value={this.state.link}
          onSubmit={this.updateLink}
          showUrlNote={this.props.showUrlNote}
        />
      </>
    )
  }

  render() {
    const { value } = this.state

    return (
      <div className="form-rich-textarea__input">
        <div className="slate-rich-text-editor">
          <Toolbar className="form-rich-textarea__toolbar">
            {this.editor && <>{this.getToolbarButtons(value)}</>}
          </Toolbar>
          <Editor
            className="form-rich-textarea__editor"
            spellCheck
            ref={this.ref}
            value={value}
            onChange={this.onChange}
            renderMark={renderMark}
            renderNode={renderNode}
            renderEditor={this.renderEditor}
            onBlur={this.onBlur}
            placeholder={<div className="placeholder">{this.props.placeholder}</div>}
          />
        </div>
      </div>
    )
  }
}

export default Slate
